import {useRef} from 'react';

import {useViewer} from '../../../state/viewer/hooks';

export interface LoggingInfo {
  userCompany: string;
  userDefaultEntity: string;
  userEntity: string;
  userOrganizations: string;
  username: string;
}

export const DEFAULT_LOGGING_INFO: LoggingInfo = {
  userCompany: '',
  userDefaultEntity: '',
  userEntity: '',
  userOrganizations: '',
  username: '',
};

/**
 * Returns a stable LoggingInfo object reference that never changes across renders.
 * This prevents unnecessary rerenders when used in dependency arrays while still
 * providing fresh data when queries execute.
 *
 * Implementation Note: This hook uses an unconventional pattern of mutating object
 * properties during render. While mutation during render is typically discouraged,
 * it's safe here because:
 * 1. We maintain referential stability - the same object is always returned
 * 2. The mutations don't trigger side effects or state updates
 * 3. The data is only read when queries execute (not during rendering)
 * 4. This prevents the cascading rerender issue that would occur with useMemo
 *    returning a new object reference on every viewer change
 */
export function useLoggingInfo(): LoggingInfo {
  const viewer = useViewer();

  // Create stable object reference on first render (never recreated)
  const loggingInfoRef = useRef<LoggingInfo>({
    ...DEFAULT_LOGGING_INFO,
  });

  // Mutate properties during render to keep values fresh
  // oxlint-disable-next-line react/refs
  const info = loggingInfoRef.current;
  info.userCompany = viewer?.userInfo?.company ?? '';
  info.userDefaultEntity = viewer?.defaultEntity?.name ?? '';
  info.userEntity = viewer?.entity ?? '';
  info.userOrganizations =
    viewer?.organizations?.map(org => org.name).join(' | ') ?? '';
  info.username = viewer?.username ?? '';

  // Always return same reference
  return info;
}
